import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomV1PickupLocationsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/pickup-locations',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/pickup-locations',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_pickup-locations';

/** Creates a new PickupLocation */
export function createPickupLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __createPickupLocation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'pickupLocation.createdDate' },
          { path: 'pickupLocation.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.CreatePickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/pickup-locations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocation.createdDate' },
              { path: 'pickupLocation.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPickupLocation;
}

/** Get a PickupLocation by id */
export function getPickupLocation(payload: object): RequestOptionsFactory<any> {
  function __getPickupLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'GET' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.GetPickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/pickup-locations/{pickupLocationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocation.createdDate' },
              { path: 'pickupLocation.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveWixEcomV1PickupLocationsUrl({
            protoPath: '/v1/pickup-locations/{pickupLocationId}',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __getPickupLocation;
}

/**
 * Update a PickupLocation
 * Delivery regions cannot be updated using this method, use AddDeliveryRegion and RemoveDeliveryRegion instead.
 */
export function updatePickupLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __updatePickupLocation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'pickupLocation.createdDate' },
          { path: 'pickupLocation.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'PATCH' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.UpdatePickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/pickup-locations/{pickupLocation.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocation.createdDate' },
              { path: 'pickupLocation.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePickupLocation;
}

/** Delete a PickupLocation */
export function deletePickupLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __deletePickupLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'DELETE' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.DeletePickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/pickup-locations/{pickupLocationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deletePickupLocation;
}

/** Query PickupLocations using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) */
export function queryPickupLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __queryPickupLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.QueryPickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/pickup-locations/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocations.createdDate' },
              { path: 'pickupLocations.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryPickupLocation;
}

/** Add a DeliveryRegion to a PickupLocation */
export function addDeliveryRegion(payload: object): RequestOptionsFactory<any> {
  function __addDeliveryRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.AddDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath:
          '/v1/pickup-locations/{pickupLocationId}/add-delivery-region',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocation.createdDate' },
              { path: 'pickupLocation.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addDeliveryRegion;
}

/** Remove a DeliveryRegion from a PickupLocation */
export function removeDeliveryRegion(
  payload: object
): RequestOptionsFactory<any> {
  function __removeDeliveryRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.RemoveDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath:
          '/v1/pickup-locations/{pickupLocationId}/remove-delivery-region',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocation.createdDate' },
              { path: 'pickupLocation.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeDeliveryRegion;
}

/** Bulk Create for new PickupLocation */
export function bulkCreatePickupLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreatePickupLocation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'pickupLocations.createdDate' },
          { path: 'pickupLocations.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.BulkCreatePickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/bulk/pickup-locations/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocations.createdDate' },
              { path: 'pickupLocations.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreatePickupLocation;
}

/** Update a PickupLocation */
export function bulkUpdatePickupLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePickupLocation({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'pickupLocations.createdDate' },
          { path: 'pickupLocations.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'PATCH' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.BulkUpdatePickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/bulk/pickup-locations/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pickupLocations.createdDate' },
              { path: 'pickupLocations.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdatePickupLocation;
}

/** Delete a PickupLocation */
export function bulkDeletePickupLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeletePickupLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.pickup_location',
      method: 'DELETE' as any,
      methodFqn: 'wix.ecom.v1.PickupLocations.BulkDeletePickupLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomV1PickupLocationsUrl({
        protoPath: '/v1/bulk/pickup-locations/delete',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __bulkDeletePickupLocation;
}
